/**
 * Erreurs du cœur crypto.
 *
 * ⚠️ INVARIANT : un message d'erreur ne contient JAMAIS de matériel secret
 * (mot de passe maître, clé, nonce, plaintext, ciphertext). Il ne décrit que la
 * classe de problème. `DecryptionFailedError` est volontairement indistinguable
 * entre « mauvaise clé » et « tag invalide » : ne pas offrir d'oracle.
 */

export type KlavoxCryptoErrorCode =
  | 'invalid_parameter'
  | 'weak_kdf_parameters'
  | 'unsupported_algorithm'
  | 'malformed_ciphertext'
  | 'decryption_failed'
  | 'secret_destroyed'
  | 'insecure_randomness';

/** Racine de toutes les erreurs levées par `@klavox/crypto-core`. */
export class KlavoxCryptoError extends Error {
  readonly code: KlavoxCryptoErrorCode;

  constructor(code: KlavoxCryptoErrorCode, message: string) {
    super(message);
    this.name = new.target.name;
    this.code = code;
    // Conserve une trace propre sur les moteurs qui le supportent.
    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(this, new.target);
    }
  }
}

/** Argument absent, mal typé ou de longueur invalide. */
export class InvalidParameterError extends KlavoxCryptoError {
  constructor(message: string) {
    super('invalid_parameter', message);
  }
}

/** Paramètres Argon2id sous le plancher de sécurité imposé. */
export class WeakKdfParametersError extends KlavoxCryptoError {
  constructor(message: string) {
    super('weak_kdf_parameters', message);
  }
}

/** Algorithme AEAD ou KDF inconnu / non autorisé. */
export class UnsupportedAlgorithmError extends KlavoxCryptoError {
  constructor(message: string) {
    super('unsupported_algorithm', message);
  }
}

/**
 * Conteneur illisible **avant** toute opération dépendant de la clé
 * (base64 invalide, version inconnue, longueur incohérente). Ne fuit rien :
 * ce diagnostic est calculable par quiconque détient le blob.
 */
export class MalformedCiphertextError extends KlavoxCryptoError {
  constructor(message: string) {
    super('malformed_ciphertext', message);
  }
}

/**
 * Échec d'authentification AEAD. Cause possible : mauvais mot de passe maître,
 * mauvaise clé, ciphertext/tag altéré, nonce déplacé, contexte ou données
 * associées incorrects. **Le message ne distingue pas ces cas.**
 */
export class DecryptionFailedError extends KlavoxCryptoError {
  constructor(message = 'dechiffrement refuse: authentification AEAD invalide') {
    super('decryption_failed', message);
  }
}

/** Usage d'une clé déjà effacée par `destroy()`. */
export class SecretDestroyedError extends KlavoxCryptoError {
  constructor(label: string) {
    super('secret_destroyed', `secret deja detruit: ${label}`);
  }
}

/** Aucun CSPRNG disponible : refus de générer du matériel cryptographique. */
export class InsecureRandomnessError extends KlavoxCryptoError {
  constructor() {
    super(
      'insecure_randomness',
      'aucune source aleatoire cryptographique disponible (crypto.getRandomValues)',
    );
  }
}
